import { nonNegativeInteger } from "../../utils/math";
import { crateCorePatterns, crateShellSkins, isPaidSupplyCrateId } from "../crates/catalog";
import { createDefaultCrateState } from "./defaults";
import type {
  PaidSupplyCrateId,
  SupplyCrateCosmeticReward,
  SupplyCrateOpenWindow,
  SupplyCrateResult,
  SupplyCrateState
} from "./types";

export function sanitizeCrates(raw: unknown): SupplyCrateState {
  const defaults = createDefaultCrateState();
  if (!isRecord(raw)) {
    return defaults;
  }

  return {
    freeReadyAtUnixSeconds: nonNegativeInteger(raw.freeReadyAtUnixSeconds),
    paidOpenWindows: {
      mini: sanitizeOpenWindow(openWindowRecord(raw.paidOpenWindows, "mini")),
      neon: sanitizeOpenWindow(openWindowRecord(raw.paidOpenWindows, "neon")),
      jackpot: sanitizeOpenWindow(openWindowRecord(raw.paidOpenWindows, "jackpot"))
    },
    pendingResult: sanitizeCrateResult(raw.pendingResult),
    lastResult: sanitizeCrateResult(raw.lastResult)
  };
}

function openWindowRecord(raw: unknown, crateId: PaidSupplyCrateId): unknown {
  if (!isRecord(raw)) {
    return null;
  }
  return raw[crateId];
}

function sanitizeOpenWindow(raw: unknown): SupplyCrateOpenWindow {
  if (!isRecord(raw)) {
    return { startUnixSeconds: 0, opens: 0 };
  }
  return {
    startUnixSeconds: nonNegativeInteger(raw.startUnixSeconds),
    opens: Math.min(5, nonNegativeInteger(raw.opens))
  };
}

function sanitizeCrateResult(raw: unknown): SupplyCrateResult | null {
  if (!isRecord(raw)) {
    return null;
  }

  const crateId = stringOr(raw.crateId, "");
  if (crateId !== "free" && !isPaidSupplyCrateId(crateId)) {
    return null;
  }

  return {
    resultId: Math.max(1, nonNegativeInteger(raw.resultId, 1)),
    crateId,
    openedAtUnixSeconds: nonNegativeInteger(raw.openedAtUnixSeconds),
    presentedAtUnixSeconds: raw.presentedAtUnixSeconds === null ? null : nonNegativeInteger(raw.presentedAtUnixSeconds),
    pricePaid: nonNegativeInteger(raw.pricePaid),
    baseValue: nonNegativeInteger(raw.baseValue),
    cash: nonNegativeInteger(raw.cash),
    jackpot: raw.jackpot === true,
    lucky: raw.lucky === true,
    ticketSeconds: sanitizeTicketSeconds(raw.ticketSeconds),
    ticketFallbackCash: nonNegativeInteger(raw.ticketFallbackCash),
    coreBurstCount: Math.min(10, nonNegativeInteger(raw.coreBurstCount)),
    overchargedCoreMultiplier: sanitizeOverchargedMultiplier(raw.overchargedCoreMultiplier),
    cosmetic: sanitizeCosmeticReward(raw.cosmetic)
  };
}

function sanitizeTicketSeconds(value: unknown): 60 | 120 | 600 | null {
  return value === 60 || value === 120 || value === 600 ? value : null;
}

function sanitizeOverchargedMultiplier(value: unknown): number | null {
  return value === 10 || value === 20 || value === 100 ? value : null;
}

function sanitizeCosmeticReward(raw: unknown): SupplyCrateCosmeticReward | null {
  if (!isRecord(raw)) {
    return null;
  }

  const kind = raw.kind === "shellSkin" || raw.kind === "corePattern" ? raw.kind : null;
  if (!kind) {
    return null;
  }

  const known = kind === "shellSkin" ? crateShellSkins : crateCorePatterns;
  const id = stringOr(raw.id, "");
  const definition = known.find((entry) => entry.id === id);
  if (!definition) {
    return null;
  }

  return {
    kind,
    id: definition.id,
    label: definition.label,
    duplicate: raw.duplicate === true,
    cashBonus: nonNegativeInteger(raw.cashBonus)
  };
}

function stringOr(value: unknown, fallback: string): string {
  return typeof value === "string" && value.length > 0 ? value : fallback;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
